import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { getSession } from 'next-auth/react';

import { PaywallGuard } from '../../components/PaywallGuard';
import CoreAPIService from '../../services/CoreAPIService';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../utils/react-query-utils';
import { provisionFlags } from '../../utils/ServerSideFeatureFlagUtils';

const DynamicPage = dynamic(
  () => import('../../components/dynamicPages/CreatePage/CreatePage'),
  {
    ssr: true,
    loading: () => <div>{/* TODO add fancy loader */}</div>,
  },
);

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const session = await getSession({ req: context.req });
  if (!session) {
    const queryClient = new QueryClient();
    return {
      props: {
        ...(await provisionFlags(context, queryClient, session)),
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context)),
        session,
      },
    };
  }

  const tenantId = context.query.tenantId as string | undefined;
  const { activeTenantId } = await CoreAPIService.fetchActiveTenantId(session.user.uid);

  if (tenantId !== activeTenantId) {
    const originalQuery = { ...context.query };
    delete originalQuery.tenantId;

    const queryString = new URLSearchParams(
      originalQuery as Record<string, string>,
    ).toString();
    const redirectUrl = `/${activeTenantId}/create${
      queryString ? `?${queryString}` : ''
    }`;

    return {
      redirect: {
        permanent: false,
        destination: redirectUrl,
      },
    };
  } else {
    const queryClient = new QueryClient();
    return {
      props: {
        ...(await provisionFlags(context, queryClient, session)),
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context)),
        session,
      },
    };
  }
};

const PageExport = () => {
  return (
    <PaywallGuard>
      <DynamicPage />
    </PaywallGuard>
  );
};
export default PageExport;
